import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { autoPublishAPI, categoriesAPI } from '../../lib/api';
import AppStoreReviewSetting from '../../components/settings/AppStoreReviewSetting';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Rss, Play, Settings, RefreshCw, Trash2, Plus, CheckCircle2, AlertTriangle,
  Clock, FileText, Layers, Power,
} from 'lucide-react';

const numberOrFallback = (v, fb) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fb;
};

export default function AdminAutoPublish() {
  const [config, setConfig] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [runs, setRuns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningNow, setRunningNow] = useState(false);
  const [tab, setTab] = useState('config');
  const reviewMode = !!config?.app_store_review_mode;

  // Add-feed form state
  const [newFeed, setNewFeed] = useState({ name: '', url: '', category_slug: '' });
  const [addingFeed, setAddingFeed] = useState(false);

  useEffect(() => { reloadAll(); }, []);

  const reloadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, feedsRes, runsRes, catsRes] = await Promise.all([
        autoPublishAPI.getConfig(),
        autoPublishAPI.listFeeds(),
        autoPublishAPI.recentRuns(10),
        categoriesAPI.list().catch(() => ({ data: { categories: [] } })),
      ]);
      setConfig(cfgRes.data);
      setFeeds(feedsRes.data.feeds || []);
      setRuns(runsRes.data.runs || []);
      setCategories(catsRes.data.categories || []);
    } catch (e) {
      toast.error('Failed to load auto-publisher state');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (patch) => {
    try {
      const res = await autoPublishAPI.updateConfig(patch);
      setConfig(res.data);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const res = await autoPublishAPI.runNow();
      const t = res.data?.totals || {};
      toast.success(
        `Published ${t.published || 0} · Skipped ${t.duplicates || 0} duplicates` +
        (t.errors ? ` · ${t.errors} errors` : '')
      );
      const runsRes = await autoPublishAPI.recentRuns(10);
      setRuns(runsRes.data.runs || []);
      setTab('runs');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setRunningNow(false);
    }
  };

  const seedDefaults = async () => {
    try {
      const res = await autoPublishAPI.seedDefaults();
      toast.success(`Seeded ${res.data.seeded} feed rows`);
      const feedsRes = await autoPublishAPI.listFeeds();
      setFeeds(feedsRes.data.feeds || []);
    } catch {
      toast.error('Seed failed');
    }
  };

  const addFeed = async () => {
    if (!newFeed.name || !newFeed.url || !newFeed.category_slug) {
      toast.error('Name, URL and category are required');
      return;
    }
    setAddingFeed(true);
    try {
      await autoPublishAPI.addFeed(newFeed);
      toast.success('Feed added');
      setNewFeed({ name: '', url: '', category_slug: '' });
      const feedsRes = await autoPublishAPI.listFeeds();
      setFeeds(feedsRes.data.feeds || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add feed');
    } finally {
      setAddingFeed(false);
    }
  };

  const toggleFeed = async (feed) => {
    try {
      await autoPublishAPI.toggleFeed(feed._id, !feed.is_active);
      setFeeds(prev => prev.map(f => f._id === feed._id ? { ...f, is_active: !f.is_active } : f));
    } catch {
      toast.error('Toggle failed');
    }
  };

  const deleteFeed = async (feed) => {
    if (!window.confirm(`Delete feed "${feed.name}"?`)) return;
    try {
      await autoPublishAPI.deleteFeed(feed._id);
      setFeeds(prev => prev.filter(f => f._id !== feed._id));
      toast.success('Feed removed');
    } catch {
      toast.error('Delete failed');
    }
  };

  const feedsByCategory = useMemo(() => {
    const map = {};
    for (const f of feeds) {
      const k = f.category_slug || 'uncategorised';
      (map[k] = map[k] || []).push(f);
    }
    return map;
  }, [feeds]);

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6" data-testid="admin-auto-publish-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Rss className="w-6 h-6 text-[#B89B5E]" /> Auto-publisher
            </h1>
            <p className="text-sm text-[#5B616E] mt-1">
              Automatically pull the latest top {config?.top_n_per_category || 10} stories per
              category from RSS and publish them live. You can still publish articles manually
              from the <a className="underline" href="/admin/articles/new">Articles editor</a>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={config?.enabled && !reviewMode ? 'default' : 'secondary'} className="gap-1">
              <Power className="w-3 h-3" />
              {reviewMode ? 'Paused · App Store review' : config?.enabled ? 'Running' : 'Paused'}
            </Badge>
            <Button
              onClick={runNow}
              disabled={runningNow || reviewMode}
              data-testid="admin-auto-publish-run-now"
              className="bg-[#1E5A8A] hover:bg-[#164568] text-white"
            >
              {runningNow ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Fetch latest now
            </Button>
          </div>
        </div>

        {/* App Store review kill-switch */}
        <AppStoreReviewSetting
          key={loading ? 'loading' : 'ready'}
          onChange={(on) => setConfig((c) => ({ ...c, app_store_review_mode: on }))}
        />

        {/* Live status KPIs */}
        <StatusKpis config={config} feeds={feeds} runs={runs} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="config" data-testid="admin-auto-publish-tab-config">
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Settings
            </TabsTrigger>
            <TabsTrigger value="feeds" data-testid="admin-auto-publish-tab-feeds">
              <Layers className="w-3.5 h-3.5 mr-1.5" /> Feeds ({feeds.length})
            </TabsTrigger>
            <TabsTrigger value="runs" data-testid="admin-auto-publish-tab-runs">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Recent runs
            </TabsTrigger>
          </TabsList>

          {/* ── SETTINGS ────────────────────────────────────────── */}
          <TabsContent value="config" className="mt-4">
            <ConfigPanel config={config} onSave={saveConfig} />
          </TabsContent>

          {/* ── FEEDS ───────────────────────────────────────────── */}
          <TabsContent value="feeds" className="mt-4 space-y-4">
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#1F7A5A]" /> Add RSS feed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Category</Label>
                    <Select
                      value={newFeed.category_slug}
                      onValueChange={(v) => setNewFeed(f => ({ ...f, category_slug: v }))}
                    >
                      <SelectTrigger data-testid="admin-auto-publish-add-feed-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.slug || c.id} value={c.slug}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input
                      placeholder="e.g. BBC Tech"
                      value={newFeed.name}
                      onChange={e => setNewFeed(f => ({ ...f, name: e.target.value }))}
                      data-testid="admin-auto-publish-add-feed-name"
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs">RSS URL</Label>
                    <Input
                      placeholder="https://example.com/rss.xml"
                      value={newFeed.url}
                      onChange={e => setNewFeed(f => ({ ...f, url: e.target.value }))}
                      data-testid="admin-auto-publish-add-feed-url"
                    />
                  </div>
                </div>
                <div className="mt-3 flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={seedDefaults}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Restore defaults
                  </Button>
                  <Button
                    size="sm"
                    onClick={addFeed}
                    disabled={addingFeed}
                    data-testid="admin-auto-publish-add-feed-submit"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add feed
                  </Button>
                </div>
              </CardContent>
            </Card>

            {Object.keys(feedsByCategory).length === 0 ? (
              <Card className="bg-white border-[#E7E0D6]">
                <CardContent className="py-10 text-center text-sm text-[#5B616E]">
                  No feeds configured. Click <span className="font-medium">Restore defaults</span> to
                  seed a curated set.
                </CardContent>
              </Card>
            ) : (
              Object.entries(feedsByCategory)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([slug, list]) => (
                  <Card key={slug} className="bg-white border-[#E7E0D6]" data-testid="admin-auto-publish-category-card">
                    <CardHeader>
                      <CardTitle className="text-sm font-medium capitalize flex items-center gap-2">
                        {slug}
                        <span className="text-[10px] font-normal text-[#5B616E]">
                          ({list.filter(f => f.is_active).length}/{list.length} active)
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="divide-y divide-[#F5F0E6]">
                        {list.map(f => (
                          <FeedRow key={f._id} feed={f} onToggle={toggleFeed} onDelete={deleteFeed} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
            )}
          </TabsContent>

          {/* ── RUNS ────────────────────────────────────────────── */}
          <TabsContent value="runs" className="mt-4">
            <RunsPanel runs={runs} onRefresh={async () => {
              const r = await autoPublishAPI.recentRuns(10);
              setRuns(r.data.runs || []);
            }} />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Status KPIs — shows cycle cadence, last run outcome, and total feeds.
function StatusKpis({ config, feeds, runs }) {
  const lastRun = runs?.[0];
  const activeFeeds = feeds.filter(f => f.is_active).length;
  const lastRunLabel = lastRun?.finished_at
    ? new Date(lastRun.finished_at).toLocaleString()
    : 'Never';
  const lastTotals = lastRun?.totals || {};
  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      <KpiTile icon={<Clock className="w-4 h-4 text-[#1E5A8A]" />} label="Cycle every" value={`${config?.interval_minutes || 15} min`} />
      <KpiTile icon={<Layers className="w-4 h-4 text-[#B89B5E]" />} label="Active feeds" value={`${activeFeeds} / ${feeds.length}`} />
      <KpiTile
        icon={<CheckCircle2 className="w-4 h-4 text-[#1F7A5A]" />}
        label="Last run"
        value={lastRun ? `${lastTotals.published || 0} published` : '—'}
        hint={lastRunLabel}
      />
      <KpiTile
        icon={<AlertTriangle className={`w-4 h-4 ${lastTotals.errors ? 'text-[#B3261E]' : 'text-[#5B616E]'}`} />}
        label="Last errors"
        value={lastTotals.errors ?? 0}
      />
    </div>
  );
}

function KpiTile({ icon, label, value, hint }) {
  return (
    <Card className="bg-white border-[#E7E0D6]">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 text-[11px] text-[#5B616E] mb-1">
          {icon}{label}
        </div>
        <p className="text-xl font-bold">{value}</p>
        {hint && <p className="text-[10px] text-[#5B616E] mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Config panel — enable toggle, interval, top_n, freshness window, byline.
function ConfigPanel({ config, onSave }) {
  const [draft, setDraft] = useState(config);
  useEffect(() => { setDraft(config); }, [config]);

  const update = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const save = () => onSave({
    enabled: !!draft.enabled,
    interval_minutes: numberOrFallback(draft.interval_minutes, 15),
    top_n_per_category: numberOrFallback(draft.top_n_per_category, 10),
    max_age_hours: numberOrFallback(draft.max_age_hours, 24),
    fetch_timeout_seconds: numberOrFallback(draft.fetch_timeout_seconds, 20),
    auto_feature_first: !!draft.auto_feature_first,
    trending_per_category: numberOrFallback(draft.trending_per_category, 2),
    trending_max_age_hours: numberOrFallback(draft.trending_max_age_hours, 12),
    byline: (draft.byline || '').trim() || 'Neural News Wire',
    fetch_source_metadata: !!draft.fetch_source_metadata,
    snippet_max_chars: numberOrFallback(draft.snippet_max_chars, 300),
    image_policy: draft.image_policy || 'feed_media',
    ai_refine: !!draft.ai_refine,
    ai_provider: draft.ai_provider || null,
    ai_model: (draft.ai_model || '').trim() || null,
    ai_min_source_chars: numberOrFallback(draft.ai_min_source_chars, 120),
    ai_min_quality_score: parseFloat(draft.ai_min_quality_score) || 0.5,
  });

  if (!draft) return null;

  return (
    <Card className="bg-white border-[#E7E0D6]">
      <CardHeader>
        <CardTitle className="text-sm font-medium">Automation settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between p-3 rounded-lg bg-[#FAF7F0] border border-[#E7E0D6]">
          <div>
            <p className="text-sm font-medium">Auto-publish enabled</p>
            <p className="text-xs text-[#5B616E]">When on, the scheduler runs every cycle and publishes new stories automatically.</p>
          </div>
          <Switch
            checked={!!draft.enabled}
            onCheckedChange={(v) => update('enabled', v)}
            data-testid="admin-auto-publish-enabled-switch"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Cycle interval (minutes)" hint="How often to fetch feeds. Minimum 5.">
            <Input
              type="number"
              min={5}
              value={draft.interval_minutes ?? 15}
              onChange={e => update('interval_minutes', e.target.value)}
              data-testid="admin-auto-publish-interval"
            />
          </Field>
          <Field label="Top N per category" hint="Maximum new articles per category per cycle.">
            <Input
              type="number"
              min={1}
              max={50}
              value={draft.top_n_per_category ?? 10}
              onChange={e => update('top_n_per_category', e.target.value)}
              data-testid="admin-auto-publish-top-n"
            />
          </Field>
          <Field label="Max age (hours)" hint="Skip items older than this — keeps the site 'latest'. 0 disables the filter.">
            <Input
              type="number"
              min={0}
              value={draft.max_age_hours ?? 24}
              onChange={e => update('max_age_hours', e.target.value)}
              data-testid="admin-auto-publish-max-age"
            />
          </Field>
          <Field label="Fetch timeout (seconds)">
            <Input
              type="number"
              min={5}
              value={draft.fetch_timeout_seconds ?? 20}
              onChange={e => update('fetch_timeout_seconds', e.target.value)}
            />
          </Field>
          <Field
            label="House byline"
            hint="Used for original content only. Syndicated articles always display the original publication and journalist."
          >
            <Input
              value={draft.byline ?? ''}
              onChange={e => update('byline', e.target.value)}
              data-testid="admin-auto-publish-byline"
            />
          </Field>
          <Field label="Auto-feature freshest" hint="Flag the newest item in each category as featured.">
            <div className="flex items-center gap-3 h-10">
              <Switch
                checked={!!draft.auto_feature_first}
                onCheckedChange={(v) => update('auto_feature_first', v)}
              />
              <span className="text-xs text-[#5B616E]">
                {draft.auto_feature_first ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </Field>
          <Field label="Trending per category" hint="Top-N freshest items per cycle marked as trending. 0 disables auto-trending.">
            <Input
              type="number"
              min={0}
              max={10}
              value={draft.trending_per_category ?? 2}
              onChange={e => update('trending_per_category', e.target.value)}
              data-testid="admin-auto-publish-trending-per-cat"
            />
          </Field>
          <Field label="Trending max age (hours)" hint="Auto-published articles older than this are demoted from trending at the start of every cycle.">
            <Input
              type="number"
              min={1}
              value={draft.trending_max_age_hours ?? 12}
              onChange={e => update('trending_max_age_hours', e.target.value)}
              data-testid="admin-auto-publish-trending-age"
            />
          </Field>
        </div>

        {/* ── Syndication pipeline ─────────────────────────────── */}
        <div className="pt-4 border-t border-[#F5F0E6] space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Syndication pipeline</h3>
            <p className="text-xs text-[#5B616E]">
              Auto-published articles are <strong>syndicated</strong>: we publish the
              original headline, a short snippet no longer than the feed's own summary,
              the original publication and byline, and a link to the full story. Full
              third-party article bodies are never stored.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Fetch attribution from source URL"
                   hint="Reads the source page for the publication name, byline and date, plus a snippet. The article body is never stored.">
              <div className="flex items-center gap-3 h-10">
                <Switch
                  checked={!!draft.fetch_source_metadata}
                  onCheckedChange={(v) => update('fetch_source_metadata', v)}
                  data-testid="admin-auto-publish-fetch-metadata"
                />
                <span className="text-xs text-[#5B616E]">
                  {draft.fetch_source_metadata ? 'Enabled' : 'Disabled (RSS metadata only)'}
                </span>
              </div>
            </Field>

            <Field label="Snippet length (characters)"
                   hint="Capped again at the length of the feed's own summary, so we never show more than the publisher syndicated.">
              <Input
                type="number"
                min={0}
                max={400}
                value={draft.snippet_max_chars ?? 300}
                onChange={e => update('snippet_max_chars', e.target.value)}
                data-testid="admin-auto-publish-snippet-chars"
              />
            </Field>

            <Field label="Hero images"
                   hint="Images are only ever taken from the feed. They are never scraped from the publisher's page.">
              <Select
                value={draft.image_policy || 'feed_media'}
                onValueChange={(v) => update('image_policy', v)}
              >
                <SelectTrigger data-testid="admin-auto-publish-image-policy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feed_media">Feed media elements only (recommended)</SelectItem>
                  <SelectItem value="feed_any">Also allow images inside feed summaries</SelectItem>
                  <SelectItem value="none">Never show an image</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="AI standfirst"
                   hint="Writes a one-line standfirst and tags from the snippet only. It never writes article bodies or rewrites headlines. Requires OPENAI_API_KEY or ANTHROPIC_API_KEY.">
              <div className="flex items-center gap-3 h-10">
                <Switch
                  checked={!!draft.ai_refine}
                  onCheckedChange={(v) => update('ai_refine', v)}
                  data-testid="admin-auto-publish-ai-refine"
                />
                <span className="text-xs text-[#5B616E]">
                  {draft.ai_refine ? 'Enabled' : 'Disabled (feed summary used as-is)'}
                </span>
              </div>
            </Field>

            <Field label="AI provider" hint="Auto-detect uses whichever key is set.">
              <Select
                value={draft.ai_provider || 'auto'}
                onValueChange={(v) => update('ai_provider', v === 'auto' ? null : v)}
              >
                <SelectTrigger data-testid="admin-auto-publish-ai-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="AI model (optional)"
                   hint="Leave blank for the provider default (gpt-4o-mini / claude-3-5-haiku).">
              <Input
                placeholder="gpt-4o-mini"
                value={draft.ai_model ?? ''}
                onChange={e => update('ai_model', e.target.value)}
                data-testid="admin-auto-publish-ai-model"
              />
            </Field>

            <Field label="Min source length (chars)"
                   hint="Don't run AI on bodies shorter than this — not worth the tokens.">
              <Input
                type="number"
                min={100}
                value={draft.ai_min_source_chars ?? 400}
                onChange={e => update('ai_min_source_chars', e.target.value)}
              />
            </Field>

            <Field label="Min AI quality score (0–1)"
                   hint="If the LLM self-rates lower than this, fall back to the fetched body.">
              <Input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={draft.ai_min_quality_score ?? 0.5}
                onChange={e => update('ai_min_quality_score', e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[#F5F0E6]">
          <Button variant="outline" onClick={() => setDraft(config)}>Reset</Button>
          <Button onClick={save} data-testid="admin-auto-publish-save-config">Save settings</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-[#5B616E]">{hint}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function FeedRow({ feed, onToggle, onDelete }) {
  const hasError = !!feed.last_error;
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{feed.name}</span>
          {!feed.is_active && <Badge variant="secondary" className="text-[10px]">Disabled</Badge>}
          {hasError && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span><AlertTriangle className="w-3.5 h-3.5 text-[#B3261E]" /></span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[320px] text-xs">
                {feed.last_error}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <a
          href={feed.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-[#5B616E] hover:text-[#1E5A8A] truncate block"
          title={feed.url}
        >
          {feed.url}
        </a>
      </div>
      <div className="text-[10px] text-[#5B616E] whitespace-nowrap hidden sm:block">
        {feed.last_checked ? new Date(feed.last_checked).toLocaleTimeString() : 'Never checked'}
      </div>
      <Switch
        checked={!!feed.is_active}
        onCheckedChange={() => onToggle(feed)}
        data-testid="admin-auto-publish-feed-toggle"
      />
      <Button
        variant="ghost"
        size="sm"
        className="text-[#B3261E] h-8 px-2"
        onClick={() => onDelete(feed)}
        data-testid="admin-auto-publish-feed-delete"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function RunsPanel({ runs, onRefresh }) {
  return (
    <Card className="bg-white border-[#E7E0D6]">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          Recent cycles
          <Button variant="ghost" size="sm" className="ml-auto" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {runs.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#5B616E]">
            No runs yet. Click <span className="font-medium">Fetch latest now</span> to trigger one.
          </div>
        ) : (
          <div className="space-y-3">
            {runs.map(run => (
              <div key={run._id} className="p-3 rounded-lg border border-[#E7E0D6] bg-[#FAF7F0]">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="text-xs text-[#5B616E]">
                    {new Date(run.started_at).toLocaleString()}
                    <span className="ml-2 text-[10px]">({(run.duration_seconds || 0).toFixed(1)}s)</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs flex-wrap">
                    <span><strong className="text-[#1F7A5A]">{run.totals?.published || 0}</strong> published</span>
                    <span className="text-[#1E5A8A]"><strong>{run.totals?.ai_refined || 0}</strong> AI-refined</span>
                    <span className="text-[#B89B5E]"><strong>{run.totals?.full_fetched || 0}</strong> attributed</span>
                    <span className="text-[#5B616E]">{run.totals?.duplicates || 0} dupes</span>
                    <span className="text-[#5B616E]">{run.totals?.fetched || 0} RSS</span>
                    {(run.totals?.errors || 0) > 0 && (
                      <span className="text-[#B3261E]">{run.totals.errors} errors</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                  {(run.per_category || []).map(c => (
                    <div key={c.category_slug} className="text-[11px] flex items-center justify-between p-1.5 rounded bg-white border border-[#E7E0D6]">
                      <span className="capitalize truncate">{c.category_slug}</span>
                      <span className="font-semibold text-[#1F7A5A]">+{c.published}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
